<script setup lang="ts">
  import { ref, watch } from 'vue';
  import type {
    OperationLogDocumentTypeFilter,
    OperationLogEventFilter,
    OperationLogFilters,
  } from '@/entities/OperationLog';
  import {
    OPERATION_LOG_DOCUMENT_TYPE_FILTER_OPTIONS,
    OPERATION_LOG_EVENT_FILTER_OPTIONS,
  } from '@/entities/OperationLog';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { DateRangePicker, FilterModal, IconImage, SelectInput, ThemeProvider } from '@/shared/ui';

  interface SelectorOption {
    id: number;
    name: string;
  }

  interface RoleOption {
    id: string;
    name: string;
  }

  const props = defineProps<{
    currentFilters?: Partial<OperationLogFilters>;
    collaboratorOptions: SelectorOption[];
    roleOptions: RoleOption[];
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: OperationLogFilters): void;
  }>();

  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const isDateRangeOpen = ref(false);
  const userId = ref<number | null>(null);
  const positionName = ref<string | null>(null);
  const documentType = ref<OperationLogDocumentTypeFilter | null>(null);
  const event = ref<OperationLogEventFilter | null>(null);

  const documentTypeOptions = OPERATION_LOG_DOCUMENT_TYPE_FILTER_OPTIONS.map((item) => ({
    id: item.value,
    name: item.label,
  }));
  const eventOptions = OPERATION_LOG_EVENT_FILTER_OPTIONS.map((item) => ({
    id: item.value,
    name: item.label,
  }));

  const formatDateRange = () => {
    if (!startDate.value && !endDate.value) {
      return 'Selecione uma data';
    }

    const formatter = new Intl.DateTimeFormat('pt-BR');
    const startLabel = startDate.value ? formatter.format(startDate.value) : '';
    const endLabel = endDate.value ? formatter.format(endDate.value) : '';

    if (startLabel && endLabel) {
      return `${startLabel} - ${endLabel}`;
    }

    return startLabel || endLabel || 'Selecione uma data';
  };

  const applyCurrentFilters = (filters?: Partial<OperationLogFilters>) => {
    startDate.value = filters?.startDate ?? null;
    endDate.value = filters?.endDate ?? null;
    userId.value = filters?.userId ?? null;
    positionName.value = filters?.positionName ?? null;
    documentType.value = filters?.documentType ?? null;
    event.value = filters?.event ?? null;
  };

  const clearLocalFilters = () => {
    applyCurrentFilters();
  };

  const toggleNumberSelection = (
    currentValue: number | null,
    nextValue: string | number | null | undefined
  ): number | null => {
    if (nextValue === null || nextValue === undefined || nextValue === '') {
      return null;
    }

    const parsedValue = Number(nextValue);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return null;
    }

    return currentValue === parsedValue ? null : parsedValue;
  };

  const toggleStringSelection = <TValue extends string>(
    currentValue: TValue | null,
    nextValue: string | number | null | undefined
  ): TValue | null => {
    if (nextValue === null || nextValue === undefined || nextValue === '') {
      return null;
    }

    const parsedValue = String(nextValue) as TValue;
    return currentValue === parsedValue ? null : parsedValue;
  };

  const applyFilters = () => {
    emit('update:filter', {
      startDate: startDate.value,
      endDate: endDate.value,
      userId: userId.value,
      positionName: positionName.value,
      documentType: documentType.value,
      event: event.value,
    });
  };

  watch(
    () => props.currentFilters,
    (newFilters) => {
      applyCurrentFilters(newFilters);
    },
    { immediate: true, deep: true }
  );
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="clearLocalFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="px-6">
          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">Data</h4>
            <button
              type="button"
              class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 min-w-[236px]"
              @click="isDateRangeOpen = true"
            >
              <IconImage :src="CalendarIcon" alt="Calendário" class="h-5 w-5" />
              <span>{{ formatDateRange() }}</span>
            </button>
            <DateRangePicker
              v-model:start-date="startDate"
              v-model:end-date="endDate"
              v-model:is-open="isDateRangeOpen"
              @apply="isDateRangeOpen = false"
              @cancel="isDateRangeOpen = false"
            />
          </div>

          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">Colaborador</h4>
            <SelectInput
              :model-value="userId ?? ''"
              :options="collaboratorOptions"
              :use-teleport="true"
              label="Selecione um colaborador"
              @update:model-value="userId = toggleNumberSelection(userId, $event)"
            />
          </div>

          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">Cargo</h4>
            <SelectInput
              :model-value="positionName ?? ''"
              :options="roleOptions"
              :use-teleport="true"
              label="Selecione um cargo"
              @update:model-value="positionName = toggleStringSelection(positionName, $event)"
            />
          </div>

          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">Tipo de Documento</h4>
            <SelectInput
              :model-value="documentType ?? ''"
              :options="documentTypeOptions"
              :use-teleport="true"
              label="Selecione um tipo de Documento"
              @update:model-value="documentType = toggleStringSelection(documentType, $event)"
            />
          </div>

          <div class="py-4">
            <h4 class="font-medium text-gray-07 mb-4">Evento</h4>
            <SelectInput
              :model-value="event ?? ''"
              :options="eventOptions"
              :use-teleport="true"
              label="Selecione um tipo de Evento"
              @update:model-value="event = toggleStringSelection(event, $event)"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
